import { component } from "picoapp";
import { TRANSITION_PRODUCT_VIEW_AFTER } from "../../Barba/Views/constants";
import { OPTION_SELECTION } from "../../StayAI/oli-subscription-box";
import { formatMoney } from "../../../utilities/general";

type StickyGaEvent = (e: MouseEvent) => void;
type StickyPositionClasses = "top" | "bottom";

const GAEvent = (productTitle: string) => () => {
  if (window.gtag) {
    window.gtag("event", "Sticky Add to Cart", {
      event_detail: productTitle,
    });
  }
};

export const stickyAddToCart = component((node: HTMLElement) => {
  let gaEvent: StickyGaEvent;
  let observer: IntersectionObserver;
  const stickyAddToCartButton = node.querySelector<HTMLButtonElement>("button");
  window.addEventListener(TRANSITION_PRODUCT_VIEW_AFTER, ({ detail }) => {
    const newProductTitle = detail.trigger.dataset.title;
    const productAvailability = detail.trigger.dataset.productAvailable;
    if (observer) {
      observer.disconnect();
    }
    createSticky(newProductTitle, productAvailability);
  });

  const createSticky = (
    productTitle?: string,
    productAvailability?: string
  ) => {
    const productForm =
      document.querySelector<HTMLFormElement>("#oli-product-form");
    const submitButton = productForm.querySelector<HTMLElement>(
      "button[type='submit']"
    );
    const anchorElement = productForm.querySelector<HTMLElement>(
      "a[data-original-one-time-price][aria-current=page]"
    );
    const productTitleEl = node.querySelector<HTMLElement>(
      "[data-product-title]"
    );
    const productPriceEl = node.querySelector<HTMLElement>("[data-price]");
    const compareAtPriceEl = node.querySelector<HTMLElement>(
      "[data-compare-at-price]"
    );
    const deliveryText = node.querySelector<HTMLElement>(
      "[data-delivery-text]"
    );
    const originalOneTimePrice = anchorElement.dataset.originalOneTimePrice;
    const oneTimePrice = anchorElement.dataset.oneTimePrice;

    const originalSellingPlanPrice =
      anchorElement.dataset.originalSellingPlanPrice;

    const sellingPlanPrice = anchorElement.dataset.sellingPlanPrice;

    const productAvailableText = node.querySelector<HTMLElement>(
      "[data-product-available-text]"
    );
    const productOOSText = node.querySelector<HTMLElement>("[data-oos-text]");
    const stickyOOSButton = node.querySelector<HTMLElement>("[data-oos-cta]");

    // Handle OOS State
    if (productAvailability === "false") {
      const anchorOOSElement = productForm.querySelector<HTMLButtonElement>(
        "button[data-action='back-in-stock']"
      );
      toggleStickyButton(false);

      observer = new IntersectionObserver(handleIntersection);
      observer.observe(anchorOOSElement);
    } else {
      toggleStickyButton(true);
    }

    function toggleStickyButton(show: boolean) {
      if (show) {
        stickyAddToCartButton.classList.remove("tw-hidden");
        productAvailableText.classList.remove("tw-hidden");
        productOOSText.classList.add("tw-hidden");
        stickyOOSButton.classList.add("tw-hidden");
        productOOSText.classList.add("tw-block");
        stickyOOSButton.classList.add("tw-block");
      } else {
        stickyAddToCartButton.classList.add("tw-hidden");
        productAvailableText.classList.add("tw-hidden");
        stickyAddToCartButton.classList.add("tw-block");
        productAvailableText.classList.add("tw-block");
        productOOSText.classList.remove("tw-hidden");
        stickyOOSButton.classList.remove("tw-hidden");
      }
    }

    if (productTitle) {
      productTitleEl.innerHTML = productTitle;
    }

    window.addEventListener(OPTION_SELECTION, ({ detail }: CustomEvent) => {
      if (detail.subscriptionPlanId === "" && !detail.frequencyText) {
        deliveryText.innerHTML = "delivered one time";
        productPriceEl.innerHTML = formatMoney(parseFloat(oneTimePrice));
        if (originalOneTimePrice > oneTimePrice) {
          compareAtPriceEl.innerHTML = formatMoney(
            parseFloat(originalOneTimePrice)
          );
          compareAtPriceEl.classList.remove("tw-hidden");
        } else {
          compareAtPriceEl.classList.add("tw-hidden");
        }
      } else {
        deliveryText.innerHTML = detail.frequencyText.toLowerCase();
        productPriceEl.innerHTML = formatMoney(parseFloat(sellingPlanPrice));
        if (originalSellingPlanPrice > sellingPlanPrice) {
          compareAtPriceEl.innerHTML = formatMoney(
            parseFloat(originalSellingPlanPrice)
          );
          compareAtPriceEl.classList.remove("tw-hidden");
        } else {
          compareAtPriceEl.classList.remove("tw-hidden");
        }
      }
    });

    if (!productForm || !submitButton || !stickyAddToCartButton) return;

    function handleIntersection(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (window.innerWidth <= 768) {
          if (!entry.isIntersecting) {
            node.setAttribute("data-active", "true");
            stickyRenderEffects("bottom");
          } else {
            node.removeAttribute("data-active");
            stickyRenderEffects();
          }
        } else {
          if (!entry.rootBounds) return;

          const scrollPosition =
            document.documentElement.scrollTop || document.body.scrollTop;
          const anchorTop = entry.boundingClientRect.top;

          const headerHeight = document.querySelector<HTMLElement>(
            "#shopify-section-header"
          ).offsetHeight;

          if (entry.isIntersecting) {
            node.removeAttribute("data-active");
            stickyRenderEffects();
          } else if (!entry.isIntersecting && scrollPosition < anchorTop) {
            node.setAttribute("data-active", "true");
            node.style.bottom = "0px";
            node.style.top = "unset";
            stickyRenderEffects("bottom");
          } else if (!entry.isIntersecting && scrollPosition > anchorTop) {
            node.setAttribute("data-active", "true");
            node.style.top = headerHeight.toString() + 'px';
            node.style.bottom = "unset";
            stickyRenderEffects("top");
          }
        }
      });
    }

    observer = new IntersectionObserver(handleIntersection);
    observer.observe(submitButton);
    handleGAEvent(productTitleEl.innerHTML);
  };

  createSticky();

  function stickyRenderEffects(position?: StickyPositionClasses) {
    const productForm = document.querySelector<HTMLElement>("#product-form-content");
    setTimeout(() => {
      document.body.classList.remove("sticky-cta-top", "sticky-cta-bottom");
      if (position && !productForm.classList.contains('.hide')) {
        document.body.classList.add(`sticky-cta-${position}`);
      }
    }, 100);
  }

  function handleGAEvent(productTitle: string) {
    if (gaEvent) {
      stickyAddToCartButton.removeEventListener("click", gaEvent);
    }
    gaEvent = GAEvent(productTitle);
    stickyAddToCartButton.addEventListener("click", gaEvent);
  }
});
